import type { ReactElement, ReactNode } from "react";

import { cn } from "@/lib/utils";

type AuthBackdropVariant = "default" | "missing";
type AuthBackdropSize = "default" | "compact" | "card";

type AuthBackdropProps = {
  /** `missing` uses smaller, unfinished sketches — used on 404. */
  variant?: AuthBackdropVariant;
  /** Tighter corner sketches for the in-app content well. */
  compact?: boolean;
  /** `card` keeps a light corner hint for small dialogs. */
  size?: AuthBackdropSize;
};

function resolveBackdropSize(
  size: AuthBackdropSize | undefined,
  compact: boolean,
): AuthBackdropSize {
  if (size) return size;
  return compact ? "compact" : "default";
}

/**
 * Construction-plan atmosphere for signed-out screens.
 * Decorative only — sits behind chrome and the form.
 */
export function AuthBackdrop({
  variant = "default",
  compact = false,
  size,
}: AuthBackdropProps): ReactElement {
  const resolvedSize = resolveBackdropSize(size, compact);
  const prefix = `${variant === "missing" ? "auth-backdrop-missing" : "auth-backdrop"}-${resolvedSize}`;
  const broken = variant === "missing";
  const isCard = resolvedSize === "card";

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div
        className={cn(
          "absolute rounded-full bg-primary/22 blur-3xl dark:bg-primary/28",
          isCard ? "-top-[40%] -left-[30%] size-[18rem]" : "-top-[28%] -left-[18%] size-[46rem]",
        )}
      />
      <div
        className={cn(
          "absolute rounded-full bg-primary/16 blur-3xl dark:bg-primary/24",
          isCard
            ? "-right-[28%] -bottom-[42%] size-[16rem]"
            : "-right-[12%] -bottom-[30%] size-[40rem]",
        )}
      />
      {isCard ? null : (
        <div
          className="absolute -bottom-[22%] -left-[10%] size-[32rem] rounded-full blur-3xl opacity-70 dark:opacity-40"
          style={{ background: "var(--concrete-50)" }}
        />
      )}

      <div
        className={
          isCard
            ? "absolute inset-0 bg-[radial-gradient(ellipse_78%_72%_at_50%_42%,hsl(var(--background))_0%,hsl(var(--background)/0.94)_42%,hsl(var(--background)/0.55)_64%,transparent_82%)]"
            : "absolute inset-0 bg-[radial-gradient(ellipse_58%_52%_at_50%_46%,hsl(var(--background))_0%,hsl(var(--background)/0.72)_36%,transparent_72%)]"
        }
      />

      <svg className="absolute inset-0 size-full text-primary" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id={`${prefix}-grid`} width="48" height="48" patternUnits="userSpaceOnUse">
            <path
              d="M48 0H0V48"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              className="opacity-[0.1] dark:opacity-[0.14]"
            />
          </pattern>
          <filter id={`${prefix}-noise`}>
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.85"
              numOctaves="4"
              stitchTiles="stitch"
            />
          </filter>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${prefix}-grid)`} />
        <rect
          width="100%"
          height="100%"
          filter={`url(#${prefix}-noise)`}
          className="opacity-[0.035] dark:opacity-[0.05]"
        />
      </svg>

      <SketchFrame
        filterId={`${prefix}-sketch-building`}
        className={
          isCard
            ? "bottom-1 left-1 w-[7.25rem] opacity-[0.22] dark:opacity-[0.28]"
            : resolvedSize === "compact"
              ? "bottom-5 left-4 w-[min(30vw,18rem)] md:bottom-6 md:left-5 md:w-[min(24vw,20rem)]"
              : broken
                ? "bottom-6 left-5 w-[min(32vw,22rem)] md:bottom-8 md:left-7 md:w-[min(26vw,22rem)]"
                : "bottom-5 left-4 w-[min(42vw,26rem)] md:bottom-7 md:left-6 md:w-[min(32vw,30rem)]"
        }
        viewBox="0 40 350 376"
      >
        {broken ? <BrokenProjectBuildingSketch /> : <ProjectBuildingSketch />}
      </SketchFrame>

      <SketchFrame
        filterId={`${prefix}-sketch-house`}
        className={
          isCard
            ? "top-1 right-1 w-[6.5rem] opacity-[0.22] dark:opacity-[0.28]"
            : resolvedSize === "compact"
              ? "top-4 right-4 w-[min(28vw,16rem)] md:top-5 md:right-5 md:w-[min(22vw,18rem)]"
              : broken
                ? "top-24 right-5 w-[min(30vw,20rem)] md:top-28 md:right-7 md:w-[min(24vw,20rem)]"
                : "top-20 right-4 w-[min(40vw,24rem)] md:top-24 md:right-6 md:w-[min(30vw,28rem)]"
        }
        viewBox="0 36 338 230"
      >
        {broken ? <BrokenHouseSketch /> : <HouseSketch />}
      </SketchFrame>
    </div>
  );
}

function SketchFrame({
  filterId,
  className,
  viewBox,
  children,
}: {
  filterId: string;
  className: string;
  viewBox: string;
  children: ReactNode;
}): ReactElement {
  return (
    <svg
      className={cn("absolute text-primary opacity-[0.36] dark:opacity-[0.42]", className)}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id={filterId} x="-3%" y="-3%" width="106%" height="106%">
          <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="2" result="noise" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale="0.55"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
      <g
        filter={`url(#${filterId})`}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </g>
    </svg>
  );
}

/** Mid-rise elevation — lower left. */
function ProjectBuildingSketch(): ReactElement {
  const bays = [106, 142, 178, 214];
  const floors = [136, 184, 232, 280];

  return (
    <g>
      <path d="M6 400 H338" strokeWidth="1.45" />
      <path d="M6 406 H220" strokeWidth="0.7" className="opacity-50" />

      <path d="M40 400 V52" strokeWidth="1.55" />
      <path d="M12 64 H214" strokeWidth="1.4" />
      <path d="M12 64 H4 M214 64 V78" strokeWidth="1.15" />
      <path d="M40 64 V78 H52 V64" strokeWidth="1.05" />
      <path d="M188 64 V148" strokeWidth="0.9" />
      <path d="M184 148 H192" strokeWidth="0.9" />
      <path d="M28 400 L40 378 L52 400" strokeWidth="1.15" />

      <path d="M88 400 V120 H252 V400" strokeWidth="1.7" />
      <path d="M88 120 H252" strokeWidth="1.2" />
      <path d="M88 112 H252 V120" strokeWidth="1.15" />
      <path d="M88 328 H252" strokeWidth="1.05" />
      <path d="M154 400 V348 H186 V400" strokeWidth="1.2" />
      <path d="M148 348 H192" strokeWidth="1.15" />
      <path d="M168 372 a3 3 0 1 0 0.1 0" strokeWidth="0.9" />

      {floors.flatMap((y) =>
        bays.map((x) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="24" height="28" strokeWidth="0.95" />
        )),
      )}
      <path
        d="M106 144 L130 156 M142 144 L166 156 M178 192 L202 204"
        strokeWidth="0.5"
        className="opacity-45"
      />

      <path d="M252 400 V268 H328 V400" strokeWidth="1.45" />
      <path d="M252 268 H328" strokeWidth="1.1" />
      <rect x="268" y="284" width="20" height="24" strokeWidth="0.9" />
      <rect x="296" y="284" width="20" height="24" strokeWidth="0.9" />
      <rect x="268" y="332" width="20" height="24" strokeWidth="0.9" />
      <rect x="296" y="332" width="20" height="24" strokeWidth="0.9" />
    </g>
  );
}

/** Contemporary house elevation — upper right. */
function HouseSketch(): ReactElement {
  return (
    <g>
      <path d="M8 248 H330" strokeWidth="1.45" />
      <path d="M48 254 H210" strokeWidth="0.7" className="opacity-50" />

      <path d="M52 138 L136 46 L220 138" strokeWidth="1.7" />
      <path d="M60 138 L136 58 L212 138" strokeWidth="0.85" className="opacity-55" />
      <path d="M60 138 V248 H212 V138" strokeWidth="1.6" />
      <path d="M60 248 H212" strokeWidth="1.2" />

      <path d="M188 88 V46 H206 V118" strokeWidth="1.25" />
      <path d="M188 46 H206" strokeWidth="1.15" />

      <rect x="76" y="154" width="52" height="56" strokeWidth="1.05" />
      <path d="M102 154 V210 M76 182 H128" strokeWidth="0.75" />
      <path d="M80 158 L124 174" strokeWidth="0.5" className="opacity-45" />

      <path d="M148 248 V176 H188 V248" strokeWidth="1.2" />
      <path d="M148 176 H188" strokeWidth="1.05" />
      <path d="M168 208 a2.5 2.5 0 1 0 0.1 0" strokeWidth="0.85" />
      <path d="M140 248 H196" strokeWidth="1.1" />
      <path d="M148 248 V256 M188 248 V256" strokeWidth="0.95" />

      <path d="M76 220 H128 M76 232 H128" strokeWidth="0.55" className="opacity-50" />

      <path d="M212 248 V168 H312 V248" strokeWidth="1.4" />
      <path d="M212 168 H312" strokeWidth="1.2" />
      <path d="M212 160 H312 V168" strokeWidth="1.05" />
      <rect x="232" y="184" width="56" height="36" strokeWidth="0.95" />
      <path d="M260 184 V220" strokeWidth="0.7" />
      <path d="M236 188 L284 204" strokeWidth="0.5" className="opacity-45" />
    </g>
  );
}

/** Mid-rise with gaps, a crack, and a fallen bay — 404. */
function BrokenProjectBuildingSketch(): ReactElement {
  return (
    <g>
      <path d="M6 400 H210" strokeWidth="1.45" />
      <path d="M248 400 H300" strokeWidth="1.45" className="opacity-70" />
      <path d="M6 406 H160" strokeWidth="0.7" className="opacity-50" />

      <path d="M40 400 V52" strokeWidth="1.55" />
      <path d="M12 64 H150" strokeWidth="1.4" />
      <path d="M178 64 H214" strokeWidth="1.4" strokeDasharray="5 5" className="opacity-65" />
      <path d="M12 64 H4" strokeWidth="1.15" />
      <path d="M40 64 V78 H52 V64" strokeWidth="1.05" />
      <path d="M188 64 V112" strokeWidth="0.9" />
      <path d="M188 128 V148" strokeWidth="0.9" strokeDasharray="3 4" />
      <path d="M28 400 L40 378 L52 400" strokeWidth="1.15" />

      <path d="M88 400 V120 H176" strokeWidth="1.7" />
      <path d="M206 120 H252 V268" strokeWidth="1.7" />
      <path d="M252 304 V400" strokeWidth="1.7" />
      <path d="M88 112 H168" strokeWidth="1.15" />
      <path d="M200 112 H252 V120" strokeWidth="1.15" />
      <path d="M88 328 H168" strokeWidth="1.05" />
      <path d="M154 400 V348 H176" strokeWidth="1.2" />
      <path d="M148 348 H176" strokeWidth="1.15" />

      <rect x="106" y="184" width="24" height="28" strokeWidth="0.95" />
      <rect x="142" y="184" width="24" height="28" strokeWidth="0.95" />
      <rect x="106" y="232" width="24" height="28" strokeWidth="0.95" />
      <rect x="142" y="280" width="24" height="28" strokeWidth="0.95" />
      <rect x="178" y="280" width="24" height="28" strokeWidth="0.95" />
      <rect x="214" y="232" width="24" height="28" strokeWidth="0.95" />

      <path d="M176 168 L198 214 L184 268 L206 318 L190 400" strokeWidth="1.15" />

      <rect
        x="214"
        y="136"
        width="24"
        height="28"
        strokeWidth="0.95"
        transform="rotate(14 226 150)"
        className="opacity-70"
      />
      <path d="M228 172 L236 188" strokeWidth="0.7" className="opacity-50" />

      <path d="M252 400 V300" strokeWidth="1.45" />
      <path d="M252 268 H290" strokeWidth="1.1" />
      <path d="M304 268 H328 V330" strokeWidth="1.1" strokeDasharray="4 5" className="opacity-60" />
      <rect x="268" y="332" width="20" height="24" strokeWidth="0.9" />
      <path d="M268 284 h20 v24" strokeWidth="0.9" strokeDasharray="3 4" className="opacity-60" />
    </g>
  );
}

/** House with a split roof, gap, and unfinished wing — 404. */
function BrokenHouseSketch(): ReactElement {
  return (
    <g>
      <path d="M8 248 H188" strokeWidth="1.45" />
      <path d="M220 248 H280" strokeWidth="1.45" className="opacity-70" />

      <path d="M52 138 L128 52" strokeWidth="1.7" />
      <path d="M148 58 L220 138" strokeWidth="1.7" />
      <path d="M128 52 L148 58" strokeWidth="1.1" strokeDasharray="3 4" className="opacity-55" />
      <path d="M60 138 L128 64" strokeWidth="0.85" className="opacity-55" />

      <path d="M60 138 V248" strokeWidth="1.6" />
      <path d="M60 248 H188" strokeWidth="1.2" />
      <path d="M212 248 V176" strokeWidth="1.6" />
      <path d="M212 138 V168" strokeWidth="1.6" strokeDasharray="4 5" className="opacity-60" />

      <path d="M198 46 H206 V70" strokeWidth="1.25" />
      <path d="M188 88 V118" strokeWidth="1.25" />
      <path
        d="M190 42 H208 V62"
        strokeWidth="1.15"
        transform="rotate(-11 199 52)"
        className="opacity-75"
      />

      <rect x="76" y="154" width="52" height="56" strokeWidth="1.05" />
      <path d="M102 154 V210" strokeWidth="0.75" />
      <path d="M76 182 H96" strokeWidth="0.75" />

      <path d="M148 248 V198" strokeWidth="1.2" />
      <path d="M148 176 H168" strokeWidth="1.05" />
      <path d="M176 176 H188 V248" strokeWidth="1.2" strokeDasharray="3 4" className="opacity-60" />
      <path d="M140 248 H176" strokeWidth="1.1" />

      <path d="M168 138 L176 176 L170 214 L186 248" strokeWidth="1.1" />

      <path d="M212 248 V200" strokeWidth="1.4" />
      <path d="M212 168 H250" strokeWidth="1.2" />
      <path d="M268 168 H312 V210" strokeWidth="1.2" strokeDasharray="4 5" className="opacity-55" />
      <rect x="232" y="184" width="28" height="36" strokeWidth="0.95" />
      <path
        d="M274 196 h22 v28"
        strokeWidth="0.95"
        transform="rotate(9 285 210)"
        className="opacity-65"
      />
    </g>
  );
}
